import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { encryptURL } from 'utils/functions';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { GarantiasContext } from 'contexts/GarantiasContext';
import { DropsContext } from 'contexts/DropsContext';
import { GridContext } from 'contexts/GridContext';
import { AuthContext } from 'contexts/AuthContext';
import { useModal } from 'components/Modals';
import ConsultaPecaModal from './Modals/ConsultaPecaModal';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import getGarantiasFilterFields from './filterFields';
import columnOverrides from './columnOverrides';

const Garantias = () => {
  const rotina = 'Garantias';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { postLoading } = useContext(DocumentosContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { garantias, getGarantias, getLoading } = useContext(GarantiasContext);
  const isAdmin = user?.nivel === 'DIRETORIA';
  const filterFields = useMemo(
    () => getGarantiasFilterFields(drops, user, isAdmin),
    [drops, isAdmin, user]
  );
  const loading = getLoading || postLoading;

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const getFilterValues = () => ({
    ...getValues(),
    ...(!isAdmin && { user: [user?.id] }),
  });

  const loadGrid = (props = {}) => {
    const filter = {
      ...(props?.filter || getValues()),
      ...(!isAdmin && { user: [user?.id] }),
    };
    closeModal();
    getGarantias({ ...props, rotina, filter });
  };

  const toolbarActions = [
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({
          rotina,
          filter: getFilterValues(),
          order: garantias?.order,
        }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({
          rotina,
          filter: getFilterValues(),
          order: garantias?.order,
        }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={garantias?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getFilterValues() },
              })
            }
          />
        ),
    },
    {
      name: 'Adicionar Garantia',
      icon: 'add',
      placement: 'primary',
      action: () => navigate('/app/Garantias/Gerar'),
    },
    {
      name: 'Consultar Peça',
      icon: 'search',
      action: () => openModal(<ConsultaPecaModal />),
    },
  ];

  const rowActions = [
    {
      name: 'Registros',
      icon: 'draw',
      action: ({ row }) =>
        navigate(`/app/Garantias/Registros/${row?.documento_id}`),
    },
    {
      name: 'Concluir',
      icon: 'check',
      show: ({ row }) => row?.situacao === 'FINALIZAR',
      action: ({ row }) =>
        navigate(`/app/Garantias/Concluir/${encryptURL(row)}`),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} colunas={garantias?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={garantias}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={loading}
          loadGrid={loadGrid}
          getRowHeight={() => 'auto'}
        />
      </Card>
    </Container>
  );
};

export default Garantias;
